import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Square, Smartphone, Monitor, Image, ChevronDown, Plus, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export type FormatType = string;

export interface FormatDefinition {
  id: string;
  labelKey?: string;
  dimensions: string;
  width: number;
  height: number;
  icon?: React.ReactNode;
  aspectRatio: string;
}

const standardFormats: FormatDefinition[] = [
  {
    id: 'square',
    labelKey: 'square',
    dimensions: '1080×1080',
    width: 1080,
    height: 1080,
    icon: <Square className="h-5 w-5" />,
    aspectRatio: '1:1',
  },
  {
    id: 'instagram',
    labelKey: 'instagram',
    dimensions: '1080×1350',
    width: 1080,
    height: 1350,
    icon: <Image className="h-5 w-5" />,
    aspectRatio: '4:5',
  },
  {
    id: 'portrait',
    labelKey: 'portrait',
    dimensions: '1080×1920',
    width: 1080,
    height: 1920,
    icon: <Smartphone className="h-5 w-5" />,
    aspectRatio: '9:16',
  },
  {
    id: 'horizontal',
    labelKey: 'horizontal',
    dimensions: '1920×1080',
    width: 1920,
    height: 1080,
    icon: <Monitor className="h-5 w-5" />,
    aspectRatio: '16:9',
  },
];

const advancedFormats: FormatDefinition[] = [
  { id: 'blog_portrait', dimensions: '768×1080', width: 768, height: 1080, aspectRatio: '32:45' },
  { id: 'landscape_small', dimensions: '1080×768', width: 1080, height: 768, aspectRatio: '45:32' },
  { id: 'hero_banner', dimensions: '1920×850', width: 1920, height: 850, aspectRatio: '192:85' },
  { id: 'tall_portrait', dimensions: '1080×1789', width: 1080, height: 1789, aspectRatio: '1080:1789' },
  { id: 'print_poster', dimensions: '2250×3300', width: 2250, height: 3300, aspectRatio: '15:22' },
];

// All known formats for lookup
export const allFormats: FormatDefinition[] = [...standardFormats, ...advancedFormats];

export function getFormatDimensions(formatId: string): { width: number; height: number; aspectRatio: string } | null {
  // Check known formats
  const known = allFormats.find(f => f.id === formatId);
  if (known) return { width: known.width, height: known.height, aspectRatio: known.aspectRatio };
  // Parse custom_WxH
  const match = formatId.match(/^custom_(\d+)x(\d+)$/);
  if (match) {
    const w = parseInt(match[1]);
    const h = parseInt(match[2]);
    return { width: w, height: h, aspectRatio: `${w}:${h}` };
  }
  return null;
}

export function getFormatLabel(formatId: string, t: (section: any, key: any) => string): string {
  const std = standardFormats.find(f => f.id === formatId);
  if (std?.labelKey) return t('formats', std.labelKey as any);
  const adv = advancedFormats.find(f => f.id === formatId);
  if (adv) return adv.dimensions;
  const dims = getFormatDimensions(formatId);
  if (dims) return `${dims.width}×${dims.height}`;
  return formatId;
}

interface FormatSelectorProps {
  selected: FormatType[];
  onChange: (formats: FormatType[]) => void;
  disabled?: boolean;
}

export function FormatSelector({ selected, onChange, disabled }: FormatSelectorProps) {
  const { t } = useLanguage();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');

  const toggleFormat = (formatId: FormatType) => {
    if (disabled) return;
    if (selected.includes(formatId)) {
      onChange(selected.filter((id) => id !== formatId));
    } else {
      onChange([...selected, formatId]);
    }
  };

  const addCustomSize = () => {
    const w = parseInt(customWidth);
    const h = parseInt(customHeight);
    if (isNaN(w) || isNaN(h) || w < 100 || h < 100 || w > 4096 || h > 4096) return;
    const id = `custom_${w}x${h}`;
    if (!selected.includes(id)) {
      onChange([...selected, id]);
    }
    setCustomWidth('');
    setCustomHeight('');
  };

  const customSelected = selected.filter(id => id.startsWith('custom_'));

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-foreground">
        {t('formats', 'outputFormats')} <span className="text-muted-foreground">{t('formats', 'creditEach')}</span>
      </label>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {standardFormats.map((format) => (
          <button
            key={format.id}
            onClick={() => toggleFormat(format.id)}
            disabled={disabled}
            className={cn(
              'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200',
              selected.includes(format.id)
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:border-primary/50 bg-secondary/30',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {format.icon}
            <span className="text-sm font-medium">{t('formats', format.labelKey as any)}</span>
            <span className="text-xs text-muted-foreground">{format.dimensions}</span>
          </button>
        ))}
      </div>

      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-1">
          <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", !advancedOpen && "-rotate-90")} />
          {t('formats', 'advancedSizes')} <span className="text-xs font-normal text-muted-foreground/70 ml-1">(Experimental Feature)</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-2">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {advancedFormats.map((format) => (
              <button
                key={format.id}
                onClick={() => toggleFormat(format.id)}
                disabled={disabled}
                className={cn(
                  'flex flex-col items-center gap-1 p-3 rounded-lg border transition-all duration-200 text-xs',
                  selected.includes(format.id)
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50 bg-secondary/30',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="font-medium">{format.dimensions}</span>
              </button>
            ))}
          </div>

          {/* Custom size input */}
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder={t('formats', 'width')}
              value={customWidth}
              onChange={(e) => setCustomWidth(e.target.value)}
              disabled={disabled}
              min={100}
              max={4096}
              className="w-24 h-9 text-sm"
            />
            <span className="text-muted-foreground text-sm">×</span>
            <Input
              type="number"
              placeholder={t('formats', 'height')}
              value={customHeight}
              onChange={(e) => setCustomHeight(e.target.value)}
              disabled={disabled}
              min={100}
              max={4096}
              className="w-24 h-9 text-sm"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={addCustomSize}
              disabled={disabled || !customWidth || !customHeight}
              className="h-9"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('formats', 'add')}
            </Button>
          </div>

          {/* Show custom sizes as removable chips */}
          {customSelected.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {customSelected.map(id => {
                const dims = getFormatDimensions(id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs border border-primary/30"
                  >
                    {dims ? `${dims.width}×${dims.height}` : id}
                    <button
                      onClick={() => onChange(selected.filter(s => s !== id))}
                      disabled={disabled}
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// Export formats for use in OutputPanel (backward compat)
export const formats = standardFormats.map(f => ({
  id: f.id,
  label: f.labelKey,
  dimensions: f.dimensions,
  icon: f.icon,
  aspectRatio: f.aspectRatio,
}));
